/**
 * The explorer shell: four views, and a context bar that always says which
 * catalog version and observation context the numbers on screen came from.
 */

import { useEffect, useState } from "react";

import { useExplorerState, type ViewName } from "./lib/url-state.ts";
import { PricesView } from "./views/PricesView.tsx";
import { ChangesView } from "./views/ChangesView.tsx";
import { ModelDetailView } from "./views/ModelDetailView.tsx";
import { SourcesView } from "./views/SourcesView.tsx";

const TABS: ReadonlyArray<{ id: ViewName; label: string }> = [
  { id: "prices", label: "Prices" },
  { id: "changes", label: "Changes" },
  { id: "model", label: "Model detail" },
  { id: "sources", label: "Sources" },
];

function ThemeToggle(): React.JSX.Element {
  const [light, setLight] = useState(() => document.documentElement.classList.contains("light"));

  useEffect(() => {
    document.documentElement.classList.toggle("light", light);
    try {
      localStorage.setItem("theme", light ? "light" : "dark");
    } catch {
      // Blocked storage only costs the preference, never the render.
    }
  }, [light]);

  return (
    <button
      type="button"
      className="button"
      onClick={() => setLight((value) => !value)}
      aria-pressed={light}
    >
      {light ? "Dark theme" : "Light theme"}
    </button>
  );
}

export function App(): React.JSX.Element {
  const [state, update] = useExplorerState();

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header__inner">
          <h1 className="app-title">
            LLM API pricing
            <span>Observed public rate cards, with their evidence</span>
          </h1>
          <nav className="tabs" aria-label="Views">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className="tab"
                aria-current={state.view === tab.id ? "page" : undefined}
                onClick={() => update({ view: tab.id })}
              >
                {tab.label}
              </button>
            ))}
          </nav>
          <ThemeToggle />
        </div>
      </header>

      <main className="app-main">
        {state.view === "prices" && <PricesView state={state} update={update} />}
        {state.view === "changes" && <ChangesView state={state} update={update} />}
        {state.view === "model" && <ModelDetailView state={state} update={update} />}
        {state.view === "sources" && <SourcesView state={state} update={update} />}
      </main>
    </div>
  );
}
